import { type Application, Assets, type Container } from 'pixi.js';
import HalloweenCharacter, {
  type CharacterIndexes,
  type HalloweenCharacterConstructorOptions
} from '~/components/Halloween/lib/halloween-character';

type HalloweenCharacterFactoryCreateOptions = Omit<
  HalloweenCharacterConstructorOptions,
  'app' | 'layer' | 'baseZIndex'
>;

const internalShiftMap: Record<number, number> = {
  2: 25
};

class HalloweenCharacterFactory {
  #charactersMap: Map<number, HalloweenCharacter> = new Map();
  #app?: Application;
  #layer?: Container;
  #baseZIndex?: number;

  public createLoadBatch(characterIndexes?: CharacterIndexes[]) {
    return Promise.all(
      (characterIndexes || [1, 2, 3, 4, 5, 6]).map((characterIndex) => this.loadCharacterAssets(characterIndex))
    );
  }

  public async loadCharacterAssets(characterIndex: number) {
    return await Assets.load({
      alias: `character_${characterIndex}`,
      src: `/assets/animations/mercenary/${characterIndex}.skel`,
      data: {
        metadata: {
          spineAtlasFile: `assets/animations/mercenary/${characterIndex}.atlas`,
          imagePath: 'assets/animations/mercenary/',
          imageLoaderType: 'atlas',
          format: 'skel'
        }
      }
    }).then((asset) => {
      Assets.cache.set(`character_${characterIndex}`, asset);
    });
  }

  public init(app: Application, layer: Container, baseZIndex = 10) {
    this.#app = app;
    this.#layer = layer;
    this.#baseZIndex = baseZIndex;

    app.renderer.on('resize', () => this.$updateCharactersPositions());
  }

  public createMany(options: number[]) {
    options.forEach((characterIndex, index) =>
      this.create({
        characterIndex: characterIndex as any,
        positionInBatch: index
      })
    );
  }

  public reset() {
    this.#charactersMap.forEach((character) => {
      character.$spine?.destroy();
      character.$healthBar?.destroy();
    });

    this.#charactersMap.clear();
    this.$updateCharactersPositions();
  }

  public create(options: HalloweenCharacterFactoryCreateOptions) {
    if (!this.#app || !this.#layer) throw new Error('HalloweenCharacterFactory is not initialized');

    if (this.#charactersMap.has(options.characterIndex)) {
      return this.#charactersMap.get(options.characterIndex)!;
    }

    const character = new HalloweenCharacter({
      layer: this.#layer,
      baseZIndex: this.#baseZIndex,
      ...options
    });

    this.#charactersMap.set(options.characterIndex, character);
    return this.modifyMany([options.characterIndex]);
  }

  public modifyAll() {
    return this.modifyMany(Array.from(this.#charactersMap.keys()));
  }

  public modifyMany(indexes: number[]) {
    const characters = indexes.map((index) => this.#charactersMap.get(index)!);

    const virtualModificators = {
      createCharacterSprite: () => {
        characters.forEach((character) => character.createCharacterSprite());
        this.$updateCharactersPositions();
        return virtualModificators;
      },
      setWalkAnimation: (direction: 'left' | 'right', speed = 1) => {
        characters.forEach((character) => character.setWalkAnimation(direction, speed));
        this.$updateCharactersPositions();
        return virtualModificators;
      },
      setVisible: (visible: boolean) => {
        characters.forEach((character) => character.setVisible(visible));
        this.$updateCharactersPositions();
        return virtualModificators;
      },
      setHealth: (health: number) => {
        characters.forEach((character) => character.setHealth(health));
        this.$updateCharactersPositions();
        return virtualModificators;
      }
    };

    return virtualModificators;
  }

  public get(characterIndex: number) {
    return this.modifyMany([characterIndex]);
  }

  public $updateCharactersPositions() {
    if (!this.#app || !this.#layer) return;

    const characters = Array.from(this.#charactersMap.entries()).filter((c) => c[1].$spine?.visible ?? false);
    if (!characters.length) {
      this.#app.render();
      return;
    }

    const centerX = this.#layer.width / 2;

    const positionShift = this.#app.screen.width < 640 ? 20 * (this.#app.screen.width / 100) : 0;

    const gap = this.#app.screen.width < 640 ? -40 : -30;

    const widths = characters.map((c) => Math.round(c[1].$spine?.getBounds().width ?? 1));

    const totalWidth = widths.reduce((a, b) => a + b, 0) + gap * Math.max(0, widths.length - 1);
    let cursor = centerX - totalWidth / 2;

    const yPosOffset = this.#app.screen.width < 640 ? -60 : 0;

    characters.forEach(([characterIndex, character], idx) => {
      const spine = character.$spine!;
      const w = widths[idx];

      spine.y = this.#layer!.height / 2 + 50 + yPosOffset;
      spine.x = cursor + w / 2 + (internalShiftMap[characterIndex] ?? 0) - positionShift;

      const barOffset = character.$direction === 'left' ? -100 : 0;
      if (character.$healthBar) {
        character.$healthBar.x =
          spine.x + spine.getBounds().width / 1.5 - (internalShiftMap[characterIndex] ?? 0) + 10 + barOffset;
        character.$healthBar.y = spine.y - 20;
      }

      cursor += w + gap;
    });
  }
}

const halloweenCharacterFactory = new HalloweenCharacterFactory();
export default halloweenCharacterFactory;
